'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function FractionCalculator() {
  const t = useTranslations("tools.fractionCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Basic Fraction Calculator State
  const [basicInputs, setBasicInputs] = useState({
    num1: 2,
    den1: 7,
    num2: 3,
    den2: 8,
    operator: '+'
  });
  const [basicResult, setBasicResult] = useState<any>(null);

  // Mixed Number Calculator State
  const [mixedInputs, setMixedInputs] = useState({
    mixed1: '2 3/4',
    mixed2: '1 5/7',
    operator: '+'
  });
  const [mixedResult, setMixedResult] = useState<any>(null);

  // Simplify Fraction State
  const [simplifyInputs, setSimplifyInputs] = useState({
    num: 21,
    den: 98
  });
  const [simplifyResult, setSimplifyResult] = useState<any>(null);

  // Decimal to Fraction State
  const [decimalInput, setDecimalInput] = useState(1.375);
  const [decimalResult, setDecimalResult] = useState<any>(null);

  // Fraction to Decimal State
  const [fractionToDecimalInputs, setFractionToDecimalInputs] = useState({
    num: 2,
    den: 7
  });
  const [fractionToDecimalResult, setFractionToDecimalResult] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Calculation Functions
  const gcd = (a: number, b: number): number => {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b !== 0) {
      let t = b;
      b = a % b;
      a = t;
    }
    return a;
  };

  const simplifyFraction = (num: number, den: number) => {
    if (den === 0) return null;
    const divisor = gcd(num, den);
    num = num / divisor;
    den = den / divisor;
    if (den < 0) {
      num = -num;
      den = -den;
    }
    return { num, den };
  };

  const toMixedNumber = (num: number, den: number) => {
    const simplified = simplifyFraction(num, den);
    if (!simplified) return null;

    const whole = Math.floor(Math.abs(simplified.num) / simplified.den);
    const remainder = Math.abs(simplified.num) % simplified.den;
    const sign = simplified.num < 0 ? -1 : 1;

    return { whole: whole * sign, num: remainder, den: simplified.den };
  };

  const parseMixedNumber = (str: string) => {
    str = str.trim();
    const parts = str.split(/\s+/);

    if (parts.length === 1) {
      if (parts[0].includes('/')) {
        const [num, den] = parts[0].split('/').map(x => parseFloat(x));
        return { num, den };
      } else {
        const whole = parseFloat(parts[0]);
        return { num: whole, den: 1 };
      }
    } else if (parts.length === 2) {
      const whole = parseFloat(parts[0]);
      const [num, den] = parts[1].split('/').map(x => parseFloat(x));
      const totalNum = whole >= 0 ? whole * den + num : whole * den - num;
      return { num: totalNum, den };
    }

    return null;
  };

  const decimalToFraction = (decimal: number) => {
    const sign = decimal < 0 ? -1 : 1;
    decimal = Math.abs(decimal);

    const tolerance = 1.0E-9;
    let h1 = 1, h2 = 0, k1 = 0, k2 = 1;
    let b = decimal;

    do {
      let a = Math.floor(b);
      let aux = h1;
      h1 = a * h1 + h2;
      h2 = aux;
      aux = k1;
      k1 = a * k1 + k2;
      k2 = aux;
      b = 1 / (b - a);
    } while (Math.abs(decimal - h1 / k1) > decimal * tolerance);

    return { num: h1 * sign, den: k1 };
  };

  // Event Handlers
  const handleBasicCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const { num1, den1, num2, den2, operator } = basicInputs;

    let resultNum: number, resultDen: number;

    switch(operator) {
      case '+':
        resultNum = num1 * den2 + num2 * den1;
        resultDen = den1 * den2;
        break;
      case '-':
        resultNum = num1 * den2 - num2 * den1;
        resultDen = den1 * den2;
        break;
      case '*':
        resultNum = num1 * num2;
        resultDen = den1 * den2;
        break;
      case '/':
        resultNum = num1 * den2;
        resultDen = den1 * num2;
        break;
      default:
        return;
    }

    const simplified = simplifyFraction(resultNum, resultDen);
    if (!simplified) {
      setBasicResult({ error: t('errorDivisionByZero') });
      return;
    }

    const mixed = toMixedNumber(simplified.num, simplified.den);
    const decimal = (simplified.num / simplified.den).toFixed(6);

    setBasicResult({ simplified, mixed, decimal });
  };

  const handleMixedCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const mixed1 = parseMixedNumber(mixedInputs.mixed1);
    const mixed2 = parseMixedNumber(mixedInputs.mixed2);

    if (!mixed1 || !mixed2) {
      setMixedResult({ error: t('errorInvalidFormat') });
      return;
    }

    let resultNum: number, resultDen: number;

    switch(mixedInputs.operator) {
      case '+':
        resultNum = mixed1.num * mixed2.den + mixed2.num * mixed1.den;
        resultDen = mixed1.den * mixed2.den;
        break;
      case '-':
        resultNum = mixed1.num * mixed2.den - mixed2.num * mixed1.den;
        resultDen = mixed1.den * mixed2.den;
        break;
      case '*':
        resultNum = mixed1.num * mixed2.num;
        resultDen = mixed1.den * mixed2.den;
        break;
      case '/':
        resultNum = mixed1.num * mixed2.den;
        resultDen = mixed1.den * mixed2.num;
        break;
      default:
        return;
    }

    const simplified = simplifyFraction(resultNum, resultDen);
    if (!simplified) {
      setMixedResult({ error: t('errorDivisionByZero') });
      return;
    }

    const mixed = toMixedNumber(simplified.num, simplified.den);
    const decimal = (simplified.num / simplified.den).toFixed(6);

    setMixedResult({ simplified, mixed, decimal });
  };

  const handleSimplify = (e: React.FormEvent) => {
    e.preventDefault();
    const { num, den } = simplifyInputs;

    const simplified = simplifyFraction(num, den);
    if (!simplified) {
      setSimplifyResult({ error: t('errorDivisionByZero') });
      return;
    }

    const mixed = toMixedNumber(simplified.num, simplified.den);
    const decimal = (simplified.num / simplified.den).toFixed(6);

    setSimplifyResult({ simplified, mixed, decimal });
  };

  const handleDecimalToFraction = (e: React.FormEvent) => {
    e.preventDefault();
    const result = decimalToFraction(decimalInput);
    const simplified = simplifyFraction(result.num, result.den);

    if (!simplified) {
      setDecimalResult({ error: t('errorDivisionByZero') });
      return;
    }

    const mixed = toMixedNumber(simplified.num, simplified.den);
    const decimal = (simplified.num / simplified.den).toFixed(6);

    setDecimalResult({ simplified, mixed, decimal });
  };

  const handleFractionToDecimal = (e: React.FormEvent) => {
    e.preventDefault();
    const { num, den } = fractionToDecimalInputs;

    if (den === 0) {
      setFractionToDecimalResult({ error: t('errorDivisionByZero') });
      return;
    }

    const decimal = num / den;
    const simplified = simplifyFraction(num, den);

    setFractionToDecimalResult({ decimal, simplified });
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '2rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: '#1e40af', marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)', gap: '2rem' },
    { gridTemplateColumns: 'repeat(2, 1fr)', gap: '2rem' }
  );

  const calcCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const cardH3Style = getResponsiveStyle(
    { color: '#2563eb', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const fractionDisplayStyle = getResponsiveStyle(
    { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', margin: '1.5rem 0', flexWrap: 'wrap' as const },
    { gap: '0.5rem', margin: '1rem 0' },
    { gap: '0.75rem', margin: '1.25rem 0' },
    { gap: '1rem', margin: '1.5rem 0' }
  );

  const fractionInputStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '0.25rem'
  };

  const inputStyle = getResponsiveStyle(
    {
      padding: '0.5rem',
      border: '2px solid #cbd5e1',
      borderRadius: '0.5rem',
      textAlign: 'center' as const,
      transition: 'border-color 0.3s'
    },
    { width: '70px', fontSize: '1rem' },
    { width: '80px', fontSize: '1.1rem' },
    { width: '80px', fontSize: '1.1rem' }
  );

  const fractionLineStyle = {
    width: '80px',
    height: '2px',
    background: '#1e293b',
    margin: '0.25rem 0'
  };

  const operatorSelectStyle = getResponsiveStyle(
    {
      padding: '0.75rem',
      border: '2px solid #cbd5e1',
      borderRadius: '0.5rem',
      background: 'white',
      cursor: 'pointer',
      transition: 'border-color 0.3s'
    },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const equalsSignStyle = getResponsiveStyle(
    { fontWeight: 'bold' as const, color: '#1e293b' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const mixedNumberInputStyle = getResponsiveStyle(
    { display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '1rem 0', flexWrap: 'wrap' as const },
    { gap: '0.5rem' },
    { gap: '0.5rem' },
    { gap: '0.5rem' }
  );

  const mixedInputFieldStyle = getResponsiveStyle(
    {
      padding: '0.5rem',
      border: '2px solid #cbd5e1',
      borderRadius: '0.5rem',
      textAlign: 'center' as const,
      transition: 'border-color 0.3s'
    },
    { width: '90px', fontSize: '1rem' },
    { width: '100px', fontSize: '1rem' },
    { width: '100px', fontSize: '1rem' }
  );

  const decimalInputStyle = getResponsiveStyle(
    {
      width: '100%',
      padding: '0.75rem',
      border: '2px solid #cbd5e1',
      borderRadius: '0.5rem',
      margin: '1rem 0',
      transition: 'border-color 0.3s'
    },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.1rem' }
  );

  const buttonGroupStyle = {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1rem'
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s'
    },
    { padding: '0.75rem 1.25rem', fontSize: '0.95rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      background: '#64748b',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'background 0.2s'
    },
    { padding: '0.75rem 1.25rem', fontSize: '0.95rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      marginTop: '1.5rem'
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const resultH4Style = getResponsiveStyle(
    { color: '#2563eb', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.1rem' }
  );

  const resultDisplayStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '0.75rem',
      textAlign: 'center' as const
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const resultFractionStyle = getResponsiveStyle(
    {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      fontWeight: 'bold' as const,
      color: '#1e40af',
      margin: '1rem 0'
    },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' },
    { fontSize: '2rem' }
  );

  const resultDetailsStyle = {
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '2px solid #cbd5e1'
  };

  const resultItemStyle = getResponsiveStyle(
    { display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const resultLabelStyle = {
    color: '#64748b',
    fontWeight: 500
  };

  const resultValueStyle = {
    color: '#1e293b',
    fontWeight: 600
  };

  const errorMessageStyle = {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem'
  };

  const helpTextStyle = getResponsiveStyle(
    { color: '#64748b', marginTop: '0.5rem' },
    { fontSize: '0.8rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.875rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem'
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.5rem', lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const referenceStyle = getResponsiveStyle(
    {
      display: 'block',
      color: colors.primary,
      textDecoration: 'none',
      marginBottom: '0.5rem',
      transition: 'color 0.3s'
    },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const renderResult = (result: any) => {
    if (!result) return null;

    if (result.error) {
      return <div style={errorMessageStyle}>{result.error}</div>;
    }

    const { simplified, mixed, decimal } = result;

    return (
      <div style={resultsStyle}>
        <h4 style={resultH4Style}>{t('resultTitle')}</h4>
        <div style={resultDisplayStyle}>
          {mixed && Math.abs(mixed.whole) > 0 && mixed.num > 0 ? (
            <div style={resultFractionStyle}>
              <div>{mixed.whole < 0 ? '-' : ''}{Math.abs(mixed.whole)} {mixed.num}/{mixed.den}</div>
            </div>
          ) : (
            <div style={resultFractionStyle}>
              <div style={{ padding: '0.25rem 0' }}>{simplified.num}</div>
              <div style={{ width: '100px', height: '3px', background: '#1e40af', margin: '0.25rem 0' }}></div>
              <div style={{ padding: '0.25rem 0' }}>{simplified.den}</div>
            </div>
          )}

          <div style={resultDetailsStyle}>
            <div style={resultItemStyle}>
              <span style={resultLabelStyle}>{t('simplified')}:</span>
              <span style={resultValueStyle}>{simplified.num}/{simplified.den}</span>
            </div>
            {mixed && Math.abs(mixed.whole) > 0 && mixed.num > 0 && (
              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t('mixedNumber')}:</span>
                <span style={resultValueStyle}>{mixed.whole < 0 ? '-' : ''}{Math.abs(mixed.whole)} {mixed.num}/{mixed.den}</span>
              </div>
            )}
            <div style={resultItemStyle}>
              <span style={resultLabelStyle}>{t('decimal')}:</span>
              <span style={resultValueStyle}>{decimal}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t('toolTitle')}</h2>
          </div>

          <div style={calculatorGridStyle}>
            {/* Basic Fraction Operations */}
            <div style={calcCardStyle}>
              <h3 style={cardH3Style}>{t('basicOperationsTitle')}</h3>
              <form onSubmit={handleBasicCalculate}>
                <div style={fractionDisplayStyle}>
                  <div style={fractionInputStyle}>
                    <input
                      type="number"
                      style={inputStyle}
                      value={basicInputs.num1}
                      onChange={(e) => setBasicInputs({...basicInputs, num1: parseFloat(e.target.value) || 0})}
                      placeholder="2"
                    />
                    <div style={fractionLineStyle}></div>
                    <input
                      type="number"
                      style={inputStyle}
                      value={basicInputs.den1}
                      onChange={(e) => setBasicInputs({...basicInputs, den1: parseFloat(e.target.value) || 1})}
                      placeholder="7"
                    />
                  </div>
                  <select
                    style={operatorSelectStyle}
                    value={basicInputs.operator}
                    onChange={(e) => setBasicInputs({...basicInputs, operator: e.target.value})}
                  >
                    <option value="+">+</option>
                    <option value="-">−</option>
                    <option value="*">×</option>
                    <option value="/">÷</option>
                  </select>
                  <div style={fractionInputStyle}>
                    <input
                      type="number"
                      style={inputStyle}
                      value={basicInputs.num2}
                      onChange={(e) => setBasicInputs({...basicInputs, num2: parseFloat(e.target.value) || 0})}
                      placeholder="3"
                    />
                    <div style={fractionLineStyle}></div>
                    <input
                      type="number"
                      style={inputStyle}
                      value={basicInputs.den2}
                      onChange={(e) => setBasicInputs({...basicInputs, den2: parseFloat(e.target.value) || 1})}
                      placeholder="8"
                    />
                  </div>
                  <span style={equalsSignStyle}>=</span>
                  <span style={equalsSignStyle}>?</span>
                </div>
                <div style={buttonGroupStyle}>
                  <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                  <button
                    type="button"
                    style={clearBtnStyle}
                    onClick={() => {
                      setBasicInputs({ num1: 2, den1: 7, num2: 3, den2: 8, operator: '+' });
                      setBasicResult(null);
                    }}
                  >
                    {t('clearButton')}
                  </button>
                </div>
              </form>
              {renderResult(basicResult)}
            </div>

            {/* Mixed Numbers Calculator */}
            <div style={calcCardStyle}>
              <h3 style={cardH3Style}>{t('mixedNumbersTitle')}</h3>
              <form onSubmit={handleMixedCalculate}>
                <div style={mixedNumberInputStyle}>
                  <input
                    type="text"
                    style={mixedInputFieldStyle}
                    value={mixedInputs.mixed1}
                    onChange={(e) => setMixedInputs({...mixedInputs, mixed1: e.target.value})}
                    placeholder="2 3/4"
                  />
                  <select
                    style={operatorSelectStyle}
                    value={mixedInputs.operator}
                    onChange={(e) => setMixedInputs({...mixedInputs, operator: e.target.value})}
                  >
                    <option value="+">+</option>
                    <option value="-">−</option>
                    <option value="*">×</option>
                    <option value="/">÷</option>
                  </select>
                  <input
                    type="text"
                    style={mixedInputFieldStyle}
                    value={mixedInputs.mixed2}
                    onChange={(e) => setMixedInputs({...mixedInputs, mixed2: e.target.value})}
                    placeholder="1 5/7"
                  />
                  <span style={equalsSignStyle}>=</span>
                  <span style={equalsSignStyle}>?</span>
                </div>
                <p style={helpTextStyle}>{t('mixedNumberFormat')}</p>
                <div style={buttonGroupStyle}>
                  <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                  <button
                    type="button"
                    style={clearBtnStyle}
                    onClick={() => {
                      setMixedInputs({ mixed1: '2 3/4', mixed2: '1 5/7', operator: '+' });
                      setMixedResult(null);
                    }}
                  >
                    {t('clearButton')}
                  </button>
                </div>
              </form>
              {renderResult(mixedResult)}
            </div>

            {/* Simplify Fraction */}
            <div style={calcCardStyle}>
              <h3 style={cardH3Style}>{t('simplifyTitle')}</h3>
              <form onSubmit={handleSimplify}>
                <div style={fractionDisplayStyle}>
                  <div style={fractionInputStyle}>
                    <input
                      type="number"
                      style={inputStyle}
                      value={simplifyInputs.num}
                      onChange={(e) => setSimplifyInputs({...simplifyInputs, num: parseFloat(e.target.value) || 0})}
                      placeholder="21"
                    />
                    <div style={fractionLineStyle}></div>
                    <input
                      type="number"
                      style={inputStyle}
                      value={simplifyInputs.den}
                      onChange={(e) => setSimplifyInputs({...simplifyInputs, den: parseFloat(e.target.value) || 1})}
                      placeholder="98"
                    />
                  </div>
                  <span style={equalsSignStyle}>=</span>
                  <span style={equalsSignStyle}>?</span>
                </div>
                <div style={buttonGroupStyle}>
                  <button type="submit" style={calculateBtnStyle}>{t('simplifyButton')}</button>
                  <button
                    type="button"
                    style={clearBtnStyle}
                    onClick={() => {
                      setSimplifyInputs({ num: 21, den: 98 });
                      setSimplifyResult(null);
                    }}
                  >
                    {t('clearButton')}
                  </button>
                </div>
              </form>
              {renderResult(simplifyResult)}
            </div>

            {/* Decimal to Fraction */}
            <div style={calcCardStyle}>
              <h3 style={cardH3Style}>{t('decimalToFractionTitle')}</h3>
              <form onSubmit={handleDecimalToFraction}>
                <input
                  type="number"
                  step="any"
                  style={decimalInputStyle}
                  value={decimalInput}
                  onChange={(e) => setDecimalInput(parseFloat(e.target.value) || 0)}
                  placeholder="1.375"
                />
                <div style={buttonGroupStyle}>
                  <button type="submit" style={calculateBtnStyle}>{t('convertButton')}</button>
                  <button
                    type="button"
                    style={clearBtnStyle}
                    onClick={() => {
                      setDecimalInput(1.375);
                      setDecimalResult(null);
                    }}
                  >
                    {t('clearButton')}
                  </button>
                </div>
              </form>
              {renderResult(decimalResult)}
            </div>

            {/* Fraction to Decimal */}
            <div style={calcCardStyle}>
              <h3 style={cardH3Style}>{t('fractionToDecimalTitle')}</h3>
              <form onSubmit={handleFractionToDecimal}>
                <div style={fractionDisplayStyle}>
                  <div style={fractionInputStyle}>
                    <input
                      type="number"
                      style={inputStyle}
                      value={fractionToDecimalInputs.num}
                      onChange={(e) => setFractionToDecimalInputs({...fractionToDecimalInputs, num: parseFloat(e.target.value) || 0})}
                      placeholder="2"
                    />
                    <div style={fractionLineStyle}></div>
                    <input
                      type="number"
                      style={inputStyle}
                      value={fractionToDecimalInputs.den}
                      onChange={(e) => setFractionToDecimalInputs({...fractionToDecimalInputs, den: parseFloat(e.target.value) || 1})}
                      placeholder="7"
                    />
                  </div>
                  <span style={equalsSignStyle}>=</span>
                  <span style={equalsSignStyle}>?</span>
                </div>
                <div style={buttonGroupStyle}>
                  <button type="submit" style={calculateBtnStyle}>{t('convertButton')}</button>
                  <button
                    type="button"
                    style={clearBtnStyle}
                    onClick={() => {
                      setFractionToDecimalInputs({ num: 2, den: 7 });
                      setFractionToDecimalResult(null);
                    }}
                  >
                    {t('clearButton')}
                  </button>
                </div>
              </form>
              {fractionToDecimalResult && !fractionToDecimalResult.error && (
                <div style={resultsStyle}>
                  <h4 style={resultH4Style}>{t('resultTitle')}</h4>
                  <div style={resultDisplayStyle}>
                    <div style={{...resultFractionStyle, fontSize: screenSize === 'mobile' ? '2rem' : '2.5rem'}}>
                      {fractionToDecimalResult.decimal.toFixed(10).replace(/\.?0+$/, '')}
                    </div>
                    <div style={resultDetailsStyle}>
                      <div style={resultItemStyle}>
                        <span style={resultLabelStyle}>{t('decimal')}:</span>
                        <span style={resultValueStyle}>{fractionToDecimalResult.decimal}</span>
                      </div>
                      <div style={resultItemStyle}>
                        <span style={resultLabelStyle}>{t('simplifiedFraction')}:</span>
                        <span style={resultValueStyle}>{fractionToDecimalResult.simplified.num}/{fractionToDecimalResult.simplified.den}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {fractionToDecimalResult && fractionToDecimalResult.error && (
                <div style={errorMessageStyle}>{fractionToDecimalResult.error}</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
          <li style={listItemStyle}>{t('howToStep5')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>

        <h3 style={h3Style}>{t('topToolsTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('topTool1')}</li>
          <li style={listItemStyle}>{t('topTool2')}</li>
          <li style={listItemStyle}>{t('topTool3')}</li>
          <li style={listItemStyle}>{t('topTool4')}</li>
          <li style={listItemStyle}>{t('topTool5')}</li>
        </ul>

        <h3 style={h3Style}>{t('bestPracticesTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('bestPractice1')}</li>
          <li style={listItemStyle}>{t('bestPractice2')}</li>
          <li style={listItemStyle}>{t('bestPractice3')}</li>
          <li style={listItemStyle}>{t('bestPractice4')}</li>
          <li style={listItemStyle}>{t('bestPractice5')}</li>
        </ul>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('whatIsFractionTitle')}</h3>
        <p style={paragraphStyle}>{t('whatIsFractionParagraph')}</p>

        <h3 style={h3Style}>{t('fractionOperationsTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t('additionTitle')}:</strong> {t('additionDescription')}</li>
          <li style={listItemStyle}><strong>{t('subtractionTitle')}:</strong> {t('subtractionDescription')}</li>
          <li style={listItemStyle}><strong>{t('multiplicationTitle')}:</strong> {t('multiplicationDescription')}</li>
          <li style={listItemStyle}><strong>{t('divisionTitle')}:</strong> {t('divisionDescription')}</li>
        </ul>

        <h3 style={h3Style}>{t('mixedNumbersExplainedTitle')}</h3>
        <p style={paragraphStyle}>{t('mixedNumbersExplainedParagraph')}</p>

        <h3 style={h3Style}>{t('simplifyingFractionsTitle')}</h3>
        <p style={paragraphStyle}>{t('simplifyingFractionsParagraph')}</p>

        <h3 style={h3Style}>{t('conversionTitle')}</h3>
        <p style={paragraphStyle}>{t('conversionParagraph')}</p>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={h3Style}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={h3Style}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={h3Style}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={h3Style}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={h3Style}>{t('faq5Question')}</h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>

        <h3 style={h3Style}>{t('faq6Question')}</h3>
        <p style={paragraphStyle}>{t('faq6Answer')}</p>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a
              href="https://softuni.bg/forum/44822/unlocking-precision-in-mathematics-with-the-fraction-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceStyle}
            >
              {t('reference1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://mathgptpro.com/article/en/the-best-fraction-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceStyle}
            >
              {t('reference2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://tools24x.com/fraction-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceStyle}
            >
              {t('reference3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://nacdashboard.nara.gov/online-fraction-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceStyle}
            >
              {t('reference4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://fractioncalculator.us/online-fraction-calculator-for-instant-math-help/"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceStyle}
            >
              {t('reference5')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.toolerz.com/fraction-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceStyle}
            >
              {t('reference6')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}